import { useState, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { C } from "../../../lib/tokens.js";
import { t } from "../../../lib/data.js";
import { isAgentRole } from "../../../lib/utils.js";
import { useViewport } from "../../../lib/useViewport.js";
import { useClickOutside } from "../../../lib/useClickOutside.js";
import { useLookups } from "../../../lib/LookupsContext.jsx";
import { useToast } from "../../../lib/ToastContext.jsx";
import { useExtensionSlot } from "../../../lib/ExtensionSlotsContext.jsx";
import { Button } from "../../../components/ui/Button.jsx";
import { Modal } from "../../../components/ui/Modal.jsx";
import { PageSpinner } from "../../../components/ui/Misc.jsx";
import { StatusChip, Chip, PriorityChip, TypeChip } from "../../../components/ui/Chips.jsx";
import { Tabs } from "../../../components/ui/Tabs.jsx";
import { ActivityFeed } from "./ActivityFeed.jsx";
import { AttachmentsTab } from "./AttachmentsTab.jsx";
import { LinkedTab } from "./LinkedTab.jsx";
import { DetailPanel } from "./DetailPanel.jsx";
import { EditRequestModal } from "./EditRequestModal.jsx";
import { useAutoGrowTextarea } from "../../../lib/useAutoGrowTextarea.js";

function TransitionConfirm({ kind, message, setMessage, confidentialType, setConfidentialType, role, onConfirm, onCancel }) {
  const isClose = kind === "close";
  const isAgent = isAgentRole(role);
  const messageRef = useAutoGrowTextarea(message);
  return (
    <Modal onClose={onCancel}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: C.deepsea }}>
          {isClose ? "Mark as Resolved" : "Re-open Request"}
        </span>
        <button onClick={onCancel} style={{ border: "none", background: "none", cursor: "pointer", padding: 4, display: "flex", borderRadius: 4 }}>
          <span className="material-icons" style={{ fontSize: 18, color: C.iron }}>close</span>
        </button>
      </div>
      <div style={{ fontSize: 13, color: C.iron }}>
        {isClose ? "Add a closing note (optional)" : "Why are you re-opening this?"}
      </div>
      <textarea
        ref={messageRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={1}
        autoFocus
        placeholder={isClose ? "e.g. Confirmed fixed in production." : "e.g. Issue has reappeared after the update."}
        style={{
          fontFamily: "Roboto", fontSize: 13, padding: "8px 10px", border: `1px solid ${C.mercury}`,
          borderRadius: 4, outline: "none", resize: "none", overflowY: "auto", overflowX: "hidden",
          minHeight: 64, maxHeight: 300, lineHeight: 1.5,
          color: C.fg, background: C.platinum, boxSizing: "border-box", width: "100%",
        }}
      />
      {isAgent ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: C.iron }}>Visibility</span>
          <select
            value={confidentialType}
            onChange={(e) => setConfidentialType(e.target.value)}
            style={{
              fontFamily: "Roboto", fontSize: 12, padding: "5px 8px", borderRadius: 4,
              border: `1px solid ${C.mercury}`, background: C.platinum, color: C.fg, outline: "none",
              cursor: "pointer",
            }}
          >
            <option value="A">Public Information</option>
            <option value="C">Partner Confidential</option>
            <option value="I">Internal</option>
          </select>
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button kind="outline" onClick={onCancel}>Cancel</Button>
        <Button kind="primary" onClick={onConfirm}>{isClose ? "Mark as Resolved" : "Re-open"}</Button>
      </div>
    </Modal>
  );
}

function MenuAction({ icon, label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 14px",
        border: "none", background: hover ? C.rowHover : "transparent", cursor: "pointer",
        fontSize: 13, color: C.fg, fontFamily: "Roboto", textAlign: "left",
      }}
    >
      <span className="material-icons" style={{ fontSize: 16, color: C.iron }}>{icon}</span>
      {label}
    </button>
  );
}

export function RequestDetail({
  request, onBack, role, me, backLabel = "Back", backIcon = "arrow_back",
  onUpdate, onPostTransition, onAddComment, onRefresh, showPriority,
}) {
  const { statuses, agents = [] } = useLookups();
  const { addToast } = useToast();
  const { isMobile } = useViewport();
  // A single named extension point for one more kebab-menu row - e.g. afspafts
  // contributes "Create timesheet line" here. No extension installed -> undefined,
  // and the call sites below just skip rendering it.
  const RequestOverflowActions = useExtensionSlot('request.detail.overflowActions');
  const [tab, setTab] = useState("activity");
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(null);
  const [transitionMsg, setTransitionMsg] = useState("");
  const [transitionConfidential, setTransitionConfidential] = useState("A");
  const [editOpen, setEditOpen] = useState(false);
  const menuRef = useRef();

  const closedStatus = useMemo(() => statuses.find((s) => s.terminal || s.is_closed), [statuses]);
  const openStatus = useMemo(() => statuses.find((s) => !s.is_closed && !s.terminal), [statuses]);
  const isRequestClosed = closedStatus ? request.status === closedStatus.id : false;
  const isMe = role === "customer" && request.reporterId === me?.id;

  useClickOutside(menuRef, menuOpen, () => setMenuOpen(false));

  const commitTransition = async () => {
    const now = new Date().toISOString();
    const isClosed = pendingTransition === "close";
    const newStatus = isClosed ? (closedStatus?.id ?? request.status) : (openStatus?.id ?? request.status);
    const actorId = me?.id ?? "me";

    const events = [{ kind: "status", at: now, by: actorId, from: request.status, to: newStatus }];

    const comment = transitionMsg.trim();
    if (comment) {
      events.push({ kind: "comment", at: now, by: actorId, text: comment });
    }

    try {
      if (onPostTransition) {
        await onPostTransition(request.id, newStatus, comment || undefined, role === "agent" ? transitionConfidential : undefined);
      }

      if (onUpdate) {
        await onUpdate(request.id, {
          status: newStatus,
          progress: isClosed ? 100 : request.progress,
          updatedAt: now,
          activity: [...request.activity, ...events],
        });
      }

      if (onRefresh) {
        await onRefresh(request.id);
      }

      setPendingTransition(null);
      setTransitionMsg("");
      setTransitionConfidential("A");
    } catch (err) {
      console.error("Failed to execute status transition seamlessly", err);
      addToast("Could not update the request status.");
    }
  };

  const sendReply = async (selectedConfidentialType) => {
    if (!reply.trim()) return;

    try {
      await onAddComment?.(request.id, {
        text: reply.trim(),
        confidentialType: selectedConfidentialType || "A",
        files: attachments,
      });

      if (onRefresh) {
        await onRefresh(request.id);
      }

      setReply("");
      setAttachments([]);
    } catch (err) {
      console.error("Failed to post comment and refresh", err);
      addToast("Could not post the comment.");
    }
  };

  return (
    <>
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: C.platinum }}>
        {isMobile ? (
          // Mobile: id+title fill the row between Back and the kebab (which
          // consolidates Edit/Resolve/Reopen/Copy link, instead of 3-4 separate
          // buttons) - title truncates to one line here since it's sharing the row,
          // full text is still readable in the body below. Chips get their own
          // row underneath instead of crowding this one.
          <div style={{ background: C.tin, borderBottom: `1px solid ${C.mercury}`, padding: "10px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Button kind="subtle" icon={backIcon} onClick={onBack} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "Roboto Mono", fontSize: 10, color: C.iron, fontWeight: 500 }}>
                  #{request.docNumber ?? request.id}
                </div>
                <div style={{
                  fontFamily: "Roboto", fontSize: 14, fontWeight: 500, color: C.deepsea,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {request.summary}
                </div>
              </div>
              <div ref={menuRef} style={{ position: "relative" }}>
                <Button kind="outline" icon="more_horiz" onClick={() => setMenuOpen((v) => !v)} />
                {menuOpen && (
                  <div
                    style={{
                      position: "absolute", top: "100%", right: 0, marginTop: 4, background: C.tin,
                      border: `1px solid ${C.mercury}`, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      minWidth: 180, zIndex: 100, overflow: "hidden",
                    }}
                  >
                    {role === "agent" || (isMe && !isRequestClosed) ? (
                      <MenuAction icon="edit" label="Edit" onClick={() => { setMenuOpen(false); setEditOpen(true); }} />
                    ) : null}
                    {(isMe || role === "agent") && !isRequestClosed ? (
                      <MenuAction icon="check_circle" label="Mark as Resolved" onClick={() => { setMenuOpen(false); setPendingTransition("close"); }} />
                    ) : null}
                    {(isMe || role === "agent") && isRequestClosed ? (
                      <MenuAction icon="restart_alt" label={t("req.detail.reopen")} onClick={() => { setMenuOpen(false); setPendingTransition("reopen"); }} />
                    ) : null}
                    {role === "customer" || role === "agent" ? (
                      <MenuAction
                        icon="content_copy"
                        label="Copy link"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.href}`);
                          setMenuOpen(false);
                        }}
                      />
                    ) : null}
                    {RequestOverflowActions && (
                      <RequestOverflowActions request={request} role={role} me={me} onClose={() => setMenuOpen(false)} />
                    )}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <TypeChip id={request.type} size="sm" readOnly />
              <StatusChip id={request.status} size="sm" filled readOnly />
              {showPriority ? <PriorityChip id={request.priority} size="sm" readOnly /> : null}
              {request.fixedIn ? (
                <Chip color="#3F7A00" bg="#EEF8DD" icon="rocket_launch" size="sm">
                  Fixed in {request.fixedIn}
                </Chip>
              ) : null}
            </div>
          </div>
        ) : (
          <div style={{ background: C.tin, borderBottom: `1px solid ${C.mercury}`, padding: "14px 24px", display: "flex", alignItems: "flex-start", gap: 16 }}>
            <Button kind="subtle" icon={backIcon} onClick={onBack}>{backLabel}</Button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontFamily: "Roboto Mono", fontSize: 12, color: C.iron, fontWeight: 500 }}>
                  #{request.docNumber ?? request.id}
                </span>
                <span style={{ color: C.silver }}>·</span>
                <TypeChip id={request.type} size="sm" readOnly />
                <StatusChip id={request.status} size="sm" filled readOnly />
                {showPriority ? <PriorityChip id={request.priority} size="sm" readOnly /> : null}
                {request.fixedIn ? (
                  <Chip color="#3F7A00" bg="#EEF8DD" icon="rocket_launch" size="sm">
                    Fixed in {request.fixedIn}
                  </Chip>
                ) : null}
              </div>
              <h1 style={{ margin: 0, fontFamily: "Roboto", fontWeight: 300, fontSize: 22, color: C.deepsea, lineHeight: 1.3, overflowWrap: "break-word" }}>
                {request.summary}
              </h1>
            </div>
            <div style={{ display: "flex", gap: 8, flex: "none", alignItems: "center" }}>
              {role === "agent" || (isMe && !isRequestClosed) ? (
                <Button kind="outline" icon="edit" onClick={() => setEditOpen(true)}>
                  Edit
                </Button>
              ) : null}
              {(isMe || role === "agent") && !isRequestClosed ? (
                <Button kind="outline" icon="check_circle" onClick={() => setPendingTransition("close")}>
                  Mark as Resolved
                </Button>
              ) : null}
              {(isMe || role === "agent") && isRequestClosed ? (
                <Button kind="ghost" icon="restart_alt" onClick={() => setPendingTransition("reopen")}>
                  {t("req.detail.reopen")}
                </Button>
              ) : null}
              <div ref={menuRef} style={{ position: "relative" }}>
                <Button kind="outline" icon="more_horiz" onClick={() => setMenuOpen((v) => !v)} />
                {menuOpen && (
                  <div
                    style={{
                      position: "absolute", top: "100%", right: 0, marginTop: 4, background: C.tin,
                      border: `1px solid ${C.mercury}`, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      minWidth: 160, zIndex: 100, overflow: "hidden",
                    }}
                  >
                    {role === "customer" || role === "agent" ? (
                      <>
                        <MenuAction
                          icon="content_copy"
                          label="Copy link"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.href}`);
                            setMenuOpen(false);
                          }}
                        />
                      </>
                    ) : null}
                    {RequestOverflowActions && (
                      <RequestOverflowActions request={request} role={role} me={me} onClose={() => setMenuOpen(false)} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) 320px", alignItems: "flex-start" }}>
          <div style={{ padding: "0 24px 24px" }}>
            <Tabs
              value={tab}
              onChange={setTab}
              style={{ marginTop: 8 }}
              tabs={[
                {
                  id: "activity",
                  label: t("req.tab.activity"),
                  icon: "forum",
                  count: request.activity.filter((item) => item.kind === "comment").length,
                },
                { id: "attachments", label: t("req.tab.attachments"), icon: "attach_file", count: request.attachmentCount },
                { id: "linked", label: t("req.tab.linked"), icon: "link", count: (request.relatedId ? 1 : 0) + (request.related?.length ?? 0) },
              ]}
            />
            <div style={{ padding: "20px 0" }}>
              <div style={{ display: tab === "activity" ? "block" : "none" }}>
                <ActivityFeed
                  request={request}
                  reply={reply}
                  setReply={setReply}
                  attachments={attachments}
                  setAttachments={setAttachments}
                  onSend={sendReply}
                  role={role}
                  isMe={isMe}
                  me={me}
                  onRefresh={onRefresh}
                  onUpdate={onUpdate}
                />
              </div>

              <div style={{ display: tab === "attachments" ? "block" : "none" }}>
                <AttachmentsTab request={request} onUpdate={onUpdate} onRefresh={onRefresh} me={me} />
              </div>

              <div style={{ display: tab === "linked" ? "block" : "none" }}>
                <LinkedTab request={request} onUpdate={onUpdate} />
              </div>
            </div>
          </div>
          <aside style={{
            padding: 24, background: C.tin,
            borderLeft: isMobile ? "none" : `1px solid ${C.mercury}`,
            borderTop: isMobile ? `1px solid ${C.mercury}` : "none",
            minHeight: isMobile ? "auto" : "calc(100vh - 200px)",
          }}>
            <DetailPanel request={request} role={role} me={me} showPriority={showPriority} onUpdate={onUpdate} onRefresh={onRefresh} />
          </aside>
        </div>
      </div>
      {pendingTransition && (
        <TransitionConfirm
          kind={pendingTransition}
          message={transitionMsg}
          setMessage={setTransitionMsg}
          confidentialType={transitionConfidential}
          setConfidentialType={setTransitionConfidential}
          role={role}
          onConfirm={commitTransition}
          onCancel={() => {
            setPendingTransition(null);
            setTransitionMsg("");
            setTransitionConfidential("A");
          }}
        />
      )}
      {editOpen && (
        <EditRequestModal
          request={request}
          role={role}
          agents={agents}
          me={me}
          onUpdate={onUpdate}
          onRefresh={onRefresh}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
}

export function RequestDetailPage({ detailCache, role, me, fetchDetail, resolveRequestRef, updateRequest, postTransition, addComment, tweaks }) {
  const { id: ref } = useParams();
  const navigate = useNavigate();
  const [resolvedId, setResolvedId] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // The URL segment can be a docNumber (human-friendly, used for sharing/nav
  // whenever a request has one) or a raw internal id (fallback for requests
  // without one, and for old bookmarked/shared links) - resolve it to the real
  // id fetchDetail needs before fetching anything.
  useEffect(() => {
    let alive = true;
    setResolvedId(null);
    setNotFound(false);
    if (!ref) return;
    resolveRequestRef(ref).then((realId) => {
      if (!alive) return;
      if (realId) setResolvedId(realId);
      else setNotFound(true);
    });
    return () => { alive = false; };
  }, [ref, resolveRequestRef]);

  useEffect(() => {
    if (resolvedId) fetchDetail(resolvedId);
  }, [resolvedId, fetchDetail]);

  // Only detailCache carries the full Request — activity/comments included, fetched
  // together by fetchDetail. The list only has RequestSummary (no activity), so falling
  // back to it here would render the page before comments have actually loaded.
  const targetRequest = detailCache?.[String(resolvedId)];

  if (notFound) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, height: "100%", padding: 24 }}>
        <span className="material-icons" style={{ fontSize: 40, color: C.silver }}>search_off</span>
        <div style={{ fontSize: 15, fontWeight: 500, color: C.fg }}>Request not found</div>
        <Button kind="outline" onClick={() => navigate("/")}>Back to requests</Button>
      </div>
    );
  }

  if (!targetRequest) {
    return <PageSpinner label="Loading request…" />;
  }

  return (
    <RequestDetail
      key={targetRequest.id}
      request={targetRequest}
      role={role}
      me={me}
      onBack={() => navigate(-1)}
      onUpdate={updateRequest}
      onPostTransition={postTransition}
      onAddComment={addComment}
      onRefresh={fetchDetail}
      showPriority={tweaks.showPriority}
    />
  );
}
